import { toDateParam } from './date'
import { tt } from './i18n'

/**
 * The weekly lunch menu — types and week arithmetic, ported from legacy
 * `lib/lunch-store.ts`.
 *
 * It lives in `packages/shared` because two modules need it and §5 forbids one
 * importing the other: `manager-menu` authors the week, `manager-orders` sells
 * from it at the till.
 *
 * FB-034 lives here. Legacy computes the week's Monday with
 * `d.setDate(...)` followed by `toISOString().slice(0, 10)`, which converts a
 * *local* date to UTC before taking the day. In Bratislava summer time that is
 * two hours behind, so a manager opening the page at 00:30 on Monday is handed
 * the previous Sunday — and therefore the previous week's menu, which is the
 * one about to be replaced. Local components are used throughout instead, the
 * same rule `toDateParam` already follows.
 */

export type LunchMealType =
  | 'soup'
  | 'burger'
  | 'pizza'
  | 'bowl'
  | 'salad'
  | 'pasta-gnocchi'
  | 'pasta-bolognese'
  | 'dessert'
  | 'main'
  | (string & {})

export interface LunchMeal {
  id: string
  type: LunchMealType
  name: string
  price: number
  /** What a soup costs *with* a main, as against on its own. */
  bundlePrice?: number
  allergens: string[]
  description?: string
  /**
   * **Declared, and read by nothing** — §9.39.
   *
   * This said *"linked product, so the sale deducts stock"*, which was never true: `lunchCartLine`
   * puts `lunchMealToCartId(meal)` — a negative hash — on every daily-menu line, and that negative id
   * is precisely what tells a menu dish from a catalogue product.
   *
   * **Wired 2026-08-06 as option A** — owner decision, `FRONTEND.md` §9.39 and §9.49: linked for
   * **stock**, priced by the **menu**. The lunch editor sets it; `lunchOrderLines` puts it in the
   * order line's `parts` (§10A.14) rather than on the line, so the line keeps its negative id and
   * `ServerPricing` goes on pricing the meal from `lunch_config`.
   *
   * That indirection is the whole design. §10A.9 re-prices any line whose `productId` is positive,
   * so naming the product on the line would charge the customer the à-la-carte price the moment a
   * manager linked one — silently, on their bill. This entry said wiring it was an owner decision
   * for exactly that reason; the decision has been made and the reason is kept, because it is why
   * the id lives where it does.
   */
  productId?: number
}

/** As much of a catalogue product as a lunch description needs. */
export interface LunchLinkedProduct {
  id?: number
  description?: string | null
}

/**
 * The popis a lunch meal shows - FRONTEND.md §9.401, and the owner's rule.
 *
 * <p><b>*"popis should be visible only when its linked to produkt … popis from there"*.</b> The
 * stored `meal.description` is a paste: legacy types it into a textarea and its product picker
 * overwrites the name and the price while leaving the sentence behind, so re-pointing a row at
 * another dish keeps the previous one's words - which is how *Palacinky Džemové* came to describe a
 * *Parená buchta*. Measured on the owner's data, every one of the 45 stored descriptions is a
 * near-verbatim copy of a catalogue description, so there is one source of truth and a stale copy of
 * it.
 *
 * <p><b>`meal.description` is never displayed.</b> It is still written back on save - nothing is
 * deleted - and the manager reads it in one place only, to decide whether to warn that a meal's
 * sentence is now orphaned.
 *
 * <p>An empty string rather than `undefined`, so a caller writes `if (description !== '')` and
 * cannot accidentally render the word `undefined`; `descriptionI18n` is deliberately not consulted,
 * per the entry.
 */
export function lunchMealDescription(
  meal: Pick<LunchMeal, 'productId'>,
  products: readonly LunchLinkedProduct[],
): string {
  if (meal.productId === undefined) return ''
  const linked = products.find((product) => product.id === meal.productId)
  return (linked?.description ?? '').trim()
}

export interface LunchDay {
  dayKey: string
  dayName: string
  enabled: boolean
  meals: LunchMeal[]
}

export interface LunchWeek {
  weekId: string
  label: string
  startDate?: string
  days: LunchDay[]
}

export interface LunchSettings {
  visible: boolean
  timeFrom: string
  timeTo: string
  showWeekends: boolean
  restaurantName: string
  restaurantTagline: string
  restaurantAddress: string
  restaurantPhone: string
  restaurantEmail: string
  restaurantOpeningHours: string
  restaurantLogoUrl: string
  sectionLabels: Partial<Record<string, string>>
  /** What a soup costs alongside a main. Default 0,50 €. */
  soupBundlePrice?: number
  customSections?: LunchCustomSection[]
}

/**
 * A section the franchise named itself - §9.315, the owner's Q1.
 *
 * **The shape is legacy's, byte for byte**, because a franchise's `/settings/lunch` blob may already
 * hold sections written by the legacy editor: `type` is a `custom-<epoch>` key, `defaultPrice` is the
 * 5,90 legacy starts a new section at, and `icon`/`color` are carried through untouched even though
 * **no AGOS surface reads either** (§9.315 says why they are not offered rather than quietly dropped -
 * a legacy blob that has them must round-trip).
 */
export interface LunchCustomSection {
  type: string
  label: string
  defaultPrice: number
  icon?: string
  color?: string
}

/**
 * The franchise's own sections, read from an untyped `/settings/lunch` blob.
 *
 * **One parser, two callers with different inputs.** The lunch editor holds a typed `LunchSettings`;
 * `sectionsFor` in the order builder holds `Record<string, unknown>` straight off the wire and parses
 * defensively. Spelling this twice is how one copy gets a guard the other lacks, so both go through
 * here: anything without a non-empty `type` and `label` is dropped rather than rendered as a blank
 * heading.
 */
export function customLunchSections(raw: unknown): LunchCustomSection[] {
  if (!Array.isArray(raw)) return []
  const sections: LunchCustomSection[] = []
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue
    const row = entry as Record<string, unknown>
    const type = typeof row.type === 'string' ? row.type.trim() : ''
    const label = typeof row.label === 'string' ? row.label.trim() : ''
    if (type === '' || label === '') continue
    sections.push({
      type,
      label,
      defaultPrice: typeof row.defaultPrice === 'number' ? row.defaultPrice : CUSTOM_SECTION_PRICE,
      ...(typeof row.icon === 'string' ? { icon: row.icon } : {}),
      ...(typeof row.color === 'string' ? { color: row.color } : {}),
    })
  }
  return sections
}

/** What legacy starts a new section at, kept so a franchise sees the same first number. */
export const CUSTOM_SECTION_PRICE = 5.9

/** A new section's key. `custom-<epoch>`, which is legacy's own and is what `meal.type` stores. */
export function customSectionKey(now: number): string {
  return `custom-${now}`
}

/**
 * Whether a section already goes by this name - built-in, renamed, or custom.
 *
 * **Legacy allows a duplicate and AGOS refuses one** (§9.315): two sections with one heading print
 * twice and leave the meal rows unassignable by eye, and nothing in the editor can then tell them
 * apart to undo it. Compared case-folded and space-trimmed, because *"Lokša"* and *"lokša "* are the
 * same heading to whoever reads the printed sheet.
 */
export function sectionNameTaken(settings: LunchSettings, label: string): boolean {
  return sectionNameClash(settings, label) !== null
}

/**
 * Which section already wears this name - §9.398, the owner's *"I tried to create Šalát"*.
 *
 * <p><b>The refusal was right and unusable.</b> `Šalát` is the label of the fifth **built-in**
 * section, and the owner's own list holds one entry - `Lokša` - so *a section with this name already
 * exists* pointed at nothing they could find. The reasonable conclusion is that the editor is
 * broken, which is what they reported.
 *
 * <p>Three kinds of collision, and they are told apart because the remedy differs: a built-in is
 * <em>already there, use it</em>; a **renamed** built-in is <em>you called something else this</em>
 * - real on this franchise, whose {@code sectionLabels} swap the pizza and burger headings; and one
 * of their own is the ordinary case.
 */
export function sectionNameClash(
  settings: LunchSettings,
  label: string,
): { kind: 'builtin' | 'renamed' | 'custom'; holder: string } | null {
  const wanted = folded(label)
  if (wanted === '') return null

  for (const [type, renamed] of Object.entries(settings.sectionLabels ?? {})) {
    if (typeof renamed === 'string' && folded(renamed) === wanted) {
      const original = LUNCH_SECTIONS.find((section) => String(section.type) === type)
      // A rename back to the built-in's own label is not a rename anybody needs explaining.
      return original !== undefined && folded(original.label) !== wanted
        ? { kind: 'renamed', holder: original.label }
        : { kind: 'builtin', holder: renamed }
    }
  }
  const builtin = LUNCH_SECTIONS.find((section) => folded(section.label) === wanted)
  if (builtin !== undefined) {
    // A built-in the franchise has renamed no longer wears this name on their screens.
    const renamedAway = settings.sectionLabels?.[String(builtin.type)]
    if (typeof renamedAway !== 'string' || folded(renamedAway) === wanted) {
      return { kind: 'builtin', holder: builtin.label }
    }
  }
  const own = customLunchSections(settings.customSections).find(
    (section) => folded(section.label) === wanted,
  )
  return own === undefined ? null : { kind: 'custom', holder: own.label }
}

/** The one comparison, so the three lookups above cannot disagree about what *the same name* means. */
function folded(name: string | undefined): string {
  return (name ?? '').trim().toLocaleLowerCase('sk')
}

/** What to say about a clash, in the words the remedy needs - §9.398. */
export function sectionNameProblem(settings: LunchSettings, label: string): string {
  const clash = sectionNameClash(settings, label)
  if (clash === null) return ''
  const name = label.trim()
  if (clash.kind === 'builtin') {
    return `${name} ${tt('už existuje - je to jedna zo základných sekcií, použite ju priamo.')}`
  }
  if (clash.kind === 'renamed') {
    return `${name} ${tt('už existuje - premenovali ste na to základnú sekciu')} "${clash.holder}".`
  }
  return `${name} ${tt('už existuje medzi vašimi sekciami.')}`
}

/**
 * Every section a meal may be filed under: the eight built-ins in their printed order, then the
 * franchise's own in the order they were added.
 *
 * `productCategory` is `null` for a custom one, which is the owner's *"not only from categories
 * list"* - a section they invented has no catalogue category to narrow the product picker to.
 */
export function allLunchSections(
  settings: LunchSettings,
): Array<{ type: string; label: string; defaultPrice: number; productCategory: string | null }> {
  return [
    ...LUNCH_SECTIONS.map((section) => ({
      type: String(section.type),
      label: settings.sectionLabels?.[String(section.type)] ?? section.label,
      defaultPrice: section.defaultPrice,
      productCategory: section.productCategory,
    })),
    ...customLunchSections(settings.customSections).map((section) => ({
      type: section.type,
      label: settings.sectionLabels?.[section.type] ?? section.label,
      defaultPrice: section.defaultPrice,
      productCategory: null,
    })),
  ]
}

export interface LunchSection {
  type: LunchMealType
  label: string
  defaultCount: number
  defaultPrice: number
  /** Which product category the picker offers for this section. */
  productCategory: string | null
  color: string
}

export const LUNCH_SECTIONS: LunchSection[] = [
  { type: 'soup', label: 'Polievky', defaultCount: 2, defaultPrice: 1.5, productCategory: 'Polievky', color: 'amber' },
  { type: 'pizza', label: 'Pizza', defaultCount: 1, defaultPrice: 5.9, productCategory: 'Pizza', color: 'orange' },
  { type: 'burger', label: 'Burger', defaultCount: 1, defaultPrice: 5.9, productCategory: 'Burgre', color: 'red' },
  { type: 'bowl', label: 'Bowl', defaultCount: 1, defaultPrice: 5.9, productCategory: 'Bowle', color: 'green' },
  { type: 'salad', label: tt('Šalát'), defaultCount: 1, defaultPrice: 4.9, productCategory: tt('Šaláty'), color: 'emerald' },
  { type: 'pasta-gnocchi', label: 'Cestoviny - Gnocchi', defaultCount: 1, defaultPrice: 5.9, productCategory: 'Cestoviny', color: 'yellow' },
  { type: 'pasta-bolognese', label: tt('Cestoviny - Bolonské'), defaultCount: 1, defaultPrice: 5.9, productCategory: 'Cestoviny', color: 'amber' },
  { type: 'dessert', label: 'Dezerty', defaultCount: 1, defaultPrice: 3.5, productCategory: 'Dezerty', color: 'pink' },
]

export const ALL_WEEKDAYS = [
  { key: 'mon', name: 'Pondelok' },
  { key: 'tue', name: 'Utorok' },
  { key: 'wed', name: 'Streda' },
  { key: 'thu', name: tt('Štvrtok') },
  { key: 'fri', name: 'Piatok' },
  { key: 'sat', name: 'Sobota' },
  { key: 'sun', name: tt('Nedeľa') },
]

export const LUNCH_ALLERGENS = [
  { id: '1', name: 'Lepok (obilniny)' },
  { id: '2', name: tt('Kôrovce') },
  { id: '3', name: 'Vajcia' },
  { id: '4', name: 'Ryby' },
  { id: '5', name: tt('Arašidy') },
  { id: '6', name: tt('Sója') },
  { id: '7', name: 'Mlieko' },
  { id: '8', name: 'Orechy' },
  { id: '9', name: 'Zeler' },
  { id: '10', name: tt('Horčica') },
  { id: '11', name: tt('Sezam') },
  { id: '12', name: tt('Siričitany') },
  { id: '13', name: 'Lupina' },
  { id: '14', name: tt('Mäkkýše') },
]

export const DEFAULT_LUNCH_SETTINGS: LunchSettings = {
  visible: true,
  timeFrom: '11:00',
  timeTo: '14:00',
  showWeekends: false,
  restaurantName: 'PizzaMajstro',
  restaurantTagline: tt('Reštaurácia s tradíciou'),
  restaurantAddress: '',
  restaurantPhone: '',
  restaurantEmail: '',
  restaurantOpeningHours: tt('Pondelok – Piatok: 11:00 – 14:00'),
  restaurantLogoUrl: '',
  sectionLabels: {},
}

const DAY_OFFSET: Record<string, number> = {
  mon: 0,
  tue: 1,
  wed: 2,
  thu: 3,
  fri: 4,
  sat: 5,
  sun: 6,
}

/**
 * The Monday of the week containing `date`, as `YYYY-MM-DD`.
 *
 * Sunday counts as the *end* of its week, not the start — a Slovak week runs
 * Monday to Sunday, so a menu opened on Sunday is still that week's.
 */
export function mondayOf(date: Date): string {
  const monday = new Date(date)
  const weekday = monday.getDay()
  monday.setDate(monday.getDate() + (weekday === 0 ? -6 : 1 - weekday))
  return toDateParam(monday)
}

export function sundayOf(mondayDate: string): string {
  const sunday = parseLocalDate(mondayDate)
  sunday.setDate(sunday.getDate() + 6)
  return toDateParam(sunday)
}

/** `YYYY-MM-DD` at local midnight — `new Date(string)` would parse it as UTC. */
function parseLocalDate(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1)
}

/** `2026-07-27` → `27.7.` — the short form printed on the menu. */
export function formatShortDate(isoDate: string): string {
  const [, month, day] = isoDate.split('-')
  return `${Number.parseInt(day ?? '1', 10)}.${Number.parseInt(month ?? '1', 10)}.`
}

export function dayShortDate(weekStartDate: string | undefined, dayKey: string): string | null {
  const date = dayDateOf(weekStartDate, dayKey)
  return date === null ? null : formatShortDate(date)
}

/**
 * The calendar date a menu day falls on — `YYYY-MM-DD`, or null when the week is undated — §9.219.
 *
 * `dayShortDate` has done this arithmetic since §9.132 and thrown the date away to print a label.
 * The checkout needs the date itself: a lunch taken for another day turns *Časenka* on and pins it,
 * and `LunchMeal` carries no date of its own.
 */
export function dayDateOf(weekStartDate: string | undefined, dayKey: string): string | null {
  if (!weekStartDate) return null
  const date = parseLocalDate(weekStartDate)
  date.setDate(date.getDate() + (DAY_OFFSET[dayKey] ?? 0))
  return toDateParam(date)
}

/**
 * Which day of the menu a meal is on, by its id — §9.219.
 *
 * **Answered from the payload rather than stored on the meal.** `LunchMeal` has no date and putting
 * one on it would change a shape three screens read; the week's `startDate` and the day's `dayKey`
 * already say it, and this is the arithmetic `dayShortDate` was already doing.
 *
 * A meal on an undated week answers null: a franchise that authored one week and never dated it
 * sells from it today, and inventing a date would pin a *Časenka* to a day nobody chose.
 */
export function lunchDayOf(weeks: readonly LunchWeek[], mealId: string): string | null {
  for (const week of weeks) {
    for (const day of week.days ?? []) {
      if ((day.meals ?? []).some((meal) => meal.id === mealId)) {
        return dayDateOf(week.startDate, day.dayKey)
      }
    }
  }
  return null
}

/** `sun` … `sat` for a date, matching the day keys the week uses. */
export function dayKeyOf(date: Date): string {
  return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][date.getDay()] ?? 'mon'
}

export function emptyWeek(startDate: string, label: string): LunchWeek {
  return {
    weekId: `week_${startDate}`,
    label,
    startDate,
    days: ALL_WEEKDAYS.map((weekday) => ({
      dayKey: weekday.key,
      dayName: weekday.name,
      enabled: weekday.key !== 'sat' && weekday.key !== 'sun',
      meals: [],
    })),
  }
}

/**
 * Next week, with this week's meals already in it.
 *
 * Owner request (`what-to-do.md`): *"Musí sa na ďalší týždeň vytvárať nový týždeň z aktuálneho
 * týždňa!!"* — and the reason is obvious once you have done it once. A week is five days of several
 * courses each, and most of it repeats; starting from empty means retyping the lot every Friday.
 *
 * **Every meal gets a new id, and that is not tidiness.** `lunchMealToCartId` hashes the id into the
 * negative product id a lunch line carries in a basket and on an order. Two weeks sharing a meal id
 * would share a cart id: a line added from this week would match next week's meal, and an order placed
 * across the boundary would name the wrong dish at the wrong price. The clone is deep for the same
 * reason — a shared `allergens` array would let editing one week alter the other.
 *
 * Which days are switched on is copied too. A kitchen that does not serve Saturday this week does not
 * serve it next week either, and that is a decision worth carrying rather than resetting.
 */
export function weekFrom(source: LunchWeek, startDate: string, label: string): LunchWeek {
  return {
    weekId: `week_${startDate}`,
    label,
    startDate,
    days: source.days.map((day) => ({
      ...day,
      meals: day.meals.map((meal) => ({
        ...meal,
        id: `meal_${crypto.randomUUID()}`,
        allergens: [...meal.allergens],
      })),
    })),
  }
}

/** Fills a stored settings blob out to a complete one. */
export function normaliseLunchSettings(
  stored: Partial<LunchSettings> | null | undefined,
): LunchSettings {
  return { ...DEFAULT_LUNCH_SETTINGS, ...(stored ?? {}) }
}

/**
 * Selling from the week, rather than authoring it.
 *
 * These four are the till's half of the lunch menu, ported from legacy
 * `components/order-builder/lunch-helpers.ts`. Every one of them computed its
 * date with `toISOString().slice(0, 10)` — the same FB-034 conversion to UTC
 * that put the menu editor a week behind at half past midnight. Local
 * components throughout, via `toDateParam`.
 */

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

/**
 * A lunch meal has no product row, so the cart needs an id for it: a djb2 hash
 * of the meal's own id, forced negative so nothing mistakes it for a product.
 *
 * Ported byte-for-byte. Changing the hash would orphan every lunch line in an
 * order that is still open.
 */
export function lunchMealToCartId(meal: { id: string }): number {
  let hash = 5381
  for (let index = 0; index < meal.id.length; index += 1) {
    hash = (Math.imul(hash, 33) ^ meal.id.charCodeAt(index)) | 0
  }
  return hash >= 0 ? -(hash + 1) : hash
}

/** The meals offered on a given day, or none if that day is switched off. */
export function mealsForDate(weeks: LunchWeek[], date: string | null | undefined): LunchMeal[] {
  if (!date || weeks.length === 0) return []

  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return []

  const monday = mondayOf(parsed)
  const dayKey = DAY_KEYS[parsed.getDay()]
  // Falling back to the first week is legacy behaviour: a franchise that has
  // authored one week and not dated it still sells from it.
  const week = weeks.find((candidate) => candidate.startDate === monday) ?? weeks[0]
  const day = week?.days.find((candidate) => candidate.dayKey === dayKey)
  return day?.enabled ? day.meals : []
}

export function shiftDate(date: string, deltaDays: number): string {
  const shifted = new Date(date)
  if (Number.isNaN(shifted.getTime())) return date
  shifted.setDate(shifted.getDate() + deltaDays)
  return toDateParam(shifted)
}

const SK_DAY_NAMES = [
  tt('Nedeľa'),
  tt('Pondelok'),
  tt('Utorok'),
  tt('Streda'),
  tt('Štvrtok'),
  tt('Piatok'),
  tt('Sobota'),
]

/** `Dnes · Pondelok 29.07`, so a waiter reads the day before the date. */
export function formatDayLabel(date: string, now: Date): string {
  const target = new Date(date)
  if (Number.isNaN(target.getTime())) return date

  const midnightToday = new Date(now)
  midnightToday.setHours(0, 0, 0, 0)
  const midnightTarget = new Date(target)
  midnightTarget.setHours(0, 0, 0, 0)

  const dayName = SK_DAY_NAMES[target.getDay()] ?? ''
  const dayOfMonth = String(target.getDate()).padStart(2, '0')
  const month = String(target.getMonth() + 1).padStart(2, '0')
  const stamp = `${dayName} ${dayOfMonth}.${month}`

  const daysApart = Math.round(
    (midnightTarget.getTime() - midnightToday.getTime()) / 86_400_000,
  )
  if (daysApart === 0) return `${tt('Dnes')} · ${stamp}`
  if (daysApart === 1) return `${tt('Zajtra')} · ${stamp}`
  if (daysApart === -1) return `${tt('Včera')} · ${stamp}`
  return stamp
}

// `isSameDate` was here and is deleted — §9.35, 2026-08-06. Unlike `endOfDay` beside it, this one is
// a trap rather than a spare part.
//
// It compared `slice(0, 10)` of two strings, which is right for the date keys this module produces
// (`lunchDateKey` answers `2026-07-31`) and wrong for a timestamp: `2026-07-27T22:30:00Z` slices to
// the 27th and *is* the 28th in Bratislava. The docblock said "two dates are the same day when their
// date parts match" and never said which of the two it accepted, so its correctness rested on an
// input contract it did not state — and a caller reaching for a function called `isSameDate` is
// exactly as likely to hold a timestamp as a key.
//
// Nothing called it. Two local date keys compare with `===` and say so on the line.
